import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent, createRef } from 'react';
import PT from 'prop-types';
import { Transition } from 'react-transition-group';
import { O as Overlay } from './Overlay-d3565a8d.js';
import { T as TypeContext, O as OnCloseContext } from './Context-534ed713.js';
import { P as Portal } from '../Portal/Portal-4e001035.js';
import { P as PreventScroll$1 } from '../LocalModules/storm-ui-utils/dist/esm/PreventScroll-b8b12465-35f323e4.js';
import { T as TaktIdProvider } from '../TaktIdContext/TaktIdContext-189dc982.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-12ae0974.js';
import { g as getElementFromRef } from '../LocalModules/storm-ui-utils/dist/esm/getElementFromRef-3993f795-83939d54.js';

/* SSR workaround for nodejs, things like Element don't exists in nodejs. */
const NodeSafeElement = typeof Element !== 'undefined' ? Element : Object;
const defaultTransitionDuration = 200; // milliseconds

class Sheet extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "modalContainerRef", void 0);
    _defineProperty(this, "teardown", () => {
      const {
        toggleEl
      } = this.props;
      // Bring focus back to the toggle element
      if (toggleEl !== undefined) {
        const element = getElementFromRef(toggleEl);
        if (element !== null && element !== void 0 && element.focus) {
          element.focus();
        }
      }
    });
    this.modalContainerRef = /*#__PURE__*/createRef();
  }
  componentWillUnmount() {
    this.teardown();
  }
  render() {
    const {
      sheetElementId,
      children,
      className,
      closeButton,
      closeButtonProps,
      isOpen,
      onClose,
      padding,
      side,
      type,
      withCloseButton,
      maxSize,
      taktId,
      preventUnmountOnClose,
      transitionDuration = defaultTransitionDuration,
      ...rest
    } = this.props;
    return /*#__PURE__*/React__default.createElement(Transition, {
      timeout: transitionDuration,
      appear: true,
      in: isOpen,
      onExit: this.teardown,
      nodeRef: this.modalContainerRef
    }, state => {
      if (state === 'exited' && !preventUnmountOnClose) return null;
      return /*#__PURE__*/React__default.createElement(Portal, {
        containerId: sheetElementId
      }, /*#__PURE__*/React__default.createElement(PreventScroll$1, {
        isScrollEnabled: state === 'exited' && preventUnmountOnClose
      }, /*#__PURE__*/React__default.createElement(TaktIdProvider, {
        taktId: taktId,
        fallbackId: createStormTaktId('sheet')
      }, /*#__PURE__*/React__default.createElement(TypeContext.Provider, {
        value: type
      }, /*#__PURE__*/React__default.createElement(OnCloseContext.Provider, {
        value: onClose
      }, /*#__PURE__*/React__default.createElement(Overlay, _extends({}, rest, {
        className: className,
        closeButton: closeButton,
        closeButtonProps: closeButtonProps,
        modalContainerRef: this.modalContainerRef,
        onClose: onClose,
        padding: padding,
        state: state,
        side: side,
        type: type,
        withCloseButton: withCloseButton,
        maxSize: maxSize
      }), children))))));
    });
  }
}
_defineProperty(Sheet, "propTypes", {
  /**
   * The React nodes/nodes that are rendered as content of the Sheet.
   */
  children: PT.node.isRequired,
  /**
   * This configures the close button for the bottom sheet. Setting the type property will display
   * the corresponding type of close button above the bottom sheet. A type of icon will render the
   * "X" icon. A type of message will render the string value of the label property. The label is
   * read by a screen reader when the close button is in focus. Both type and label are required.
   */
  closeButton: PT.shape({
    type: PT.oneOf(['icon', 'message']).isRequired,
    label: PT.string.isRequired
  }).isRequired,
  /**
   * Props that are applied to the CloseButton component.
   */
  closeButtonProps: PT.objectOf(PT.any),
  /**
   * To show or hide the close button.
   */
  withCloseButton: PT.bool,
  /**
   * A passthrough prop that allows the Sheet Shroud to be customized by wrapping the
   * Sheet in a styled-component.
   */
  className: PT.string,
  /**
   * This should correspond to the ROOT level dom node id that the modal will render into.
   * When this prop is not explicitly passed a value, the modal will mount the bottom sheet into
   * the div with id matching the portalElementId property in the popover object of the theme.
   */
  sheetElementId: PT.string,
  /**
   * Control open / close of bottom sheet. It will animate automatically on change.
   */
  isOpen: PT.bool,
  /**
   * Fired when any close action occurs, such as: clicking on the background, hitting the close
   * button, or hitting escape.
   */
  onClose: PT.func,
  /**
   * Pass a ref to an element here to use as the toggle element. Focus will move back to this
   * element when the bottom sheet closes.
   */
  toggleEl: PT.oneOfType([PT.instanceOf(NodeSafeElement), PT.shape({
    current: PT.instanceOf(NodeSafeElement)
  })]),
  /**
   * Sets the Sheet background color.
   */
  type: PT.oneOf(['light', 'dark', 'blue']),
  /**
   * Configure the side of the screen the sheet will appear.
   */
  side: PT.oneOf(['bottom', 'start', 'end']),
  /**
   * Sets the padding around the sheet's content.
   */
  padding: PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge']),
  /**
   * When set to `viewport`, the Sheet will fill up to 100% of the viewport width for `start` and
   * `end` sides or up to 100% of the viewport height for `bottom` side. This is useful for
   * applications that control the Sheet size based on the width or height of the content.
   *
   * When set to `default` the Sheet will fill up to 80% of the viewport width for `start` and
   * `end` sides or up to 65% of the viewport height for `bottom` side.
   */
  maxSize: PT.oneOf(['viewport', 'default']),
  /**
  * Boolean value to leave child component mounted when sheet is exited/closed.
  */
  preventUnmountOnClose: PT.bool,
  header: PT.node,
  transitionDuration: PT.number
});
_defineProperty(Sheet, "defaultProps", {
  sheetElementId: undefined,
  className: '',
  closeButtonProps: undefined,
  isOpen: false,
  onClose: undefined,
  padding: 'base',
  side: 'bottom',
  toggleEl: undefined,
  type: 'light',
  withCloseButton: true,
  maxSize: 'default',
  preventUnmountOnClose: false,
  header: undefined,
  transitionDuration: defaultTransitionDuration
});
var Sheet$1 = Sheet;

export { Sheet$1 as S };
//# sourceMappingURL=Sheet-38988bae.js.map
